import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';

import styled from '@emotion/styled';

import StatusCell from '../../../components/Badge/StatusCell';
import EmptyState from '../../../components/Cards/EmptyState';
import KycRequired from '../../../components/Cards/KycRequired';
import GiftBagIcon from '../../../components/Icons/GiftBagIcon';
import WarnIcon from '../../../components/Icons/WarnIcon';
import { Modal } from '../../../components/Modal/Modal';
import DispatcherActiveCompanyModal from '../../../components/Modal/ModalActions/DispatcherActiveCompanyModal/DispatcherActiveCompanyModal';
import DispatcherPreviousCompanyModal from '../../../components/Modal/ModalActions/DispatcherPreviousCompanyModal/DispatcherPreviousCompanyModal';
import KycInformationModal from '../../../components/Modal/ModalActions/KycInformationModal/KycInformationModal';
import Tooltip from '../../../components/Tooltip/Tooltip';
import useGetDriverWorkHistoryInfo from '../../../hooks/dashboard/driver-lookup/useGetDriverWorkHistoryInfo';
import { useAppSelector } from '../../../redux/store';

interface DriverLookupDataBreakdownProps {
  ninNumber?: string;
}

const DriverLookupDataBreakdown: React.FC<DriverLookupDataBreakdownProps> = ({
  ninNumber,
}) => {
  const { kycStatusData } = useAppSelector((state) => state.kycStatusData);
  const { jsonData } = useParams();
  const objectFyData = jsonData
    ? JSON.parse(decodeURIComponent(jsonData ?? ''))
    : null;

  const [showActiveCompanyModal, setShowActiveCompanyModal] = useState(false);
  const [showPreviousCompanyModal, setShowPreviousCompanyModal] =
    useState(false);

  const [
    showKycRequiredViewActiveCompanyModal,
    setShowKycRequiredViewActiveCompanyModal,
  ] = useState(false);
  const [
    showKycRequiredViewPreviousCompanyModal,
    setShowKycRequiredViewPreviousCompanyModal,
  ] = useState(false);

  const [
    showKycSetupViewActiveCompanyModal,
    setShowKycSetupViewActiveCompanyModal,
  ] = useState(false);
  const [
    showKycSetupViewPreviousCompanyModal,
    setShowKycSetupViewPreviousCompanyModal,
  ] = useState(false);

  const {
    getDriverWorkHistoryInfo,
    loading: loadingWorkHistory,
    historyInfoData,
  } = useGetDriverWorkHistoryInfo();

  useEffect(() => {
    if (objectFyData?.id) {
      getDriverWorkHistoryInfo(objectFyData.id);
    }
    // eslint-disable-next-line
  }, [jsonData]);

  const onShowActiveCompanyModal = () => {
    !['Accepted'].includes(kycStatusData?.kycStatus ?? '')
      ? setShowKycRequiredViewActiveCompanyModal(true)
      : setShowActiveCompanyModal(true);
  };

  const onShowPreviousCompanyModal = () => {
    !['Accepted'].includes(kycStatusData?.kycStatus ?? '')
      ? setShowKycRequiredViewPreviousCompanyModal(true)
      : setShowPreviousCompanyModal(true);
  };

  const successActionFnViewActiveCompany = () => {
    setShowKycRequiredViewActiveCompanyModal(false);
    setShowKycSetupViewActiveCompanyModal(true);
  };

  const successActionFnViewPreviousCompany = () => {
    setShowKycRequiredViewPreviousCompanyModal(false);
    setShowKycSetupViewPreviousCompanyModal(true);
  };

  return (
    <Container>
      {/* <Label>Overview</Label> */}
      {/* Section 2 */}
      <SectionInfo>
        <LabelArea>Driver Information</LabelArea>
        <GreyArea>
          <Column>
            <Row>
              <div>First name</div>
              <div>{objectFyData?.firstName || 'N/A'}</div>
            </Row>
            <Row>
              <div>Last Name</div>
              <div>{objectFyData?.lastName || 'N/A'}</div>
            </Row>
            <Row>
              <div>Gender</div>
              <div>{objectFyData?.gender || 'N/A'}</div>
            </Row>
            <Row>
              <div>Category of fleets</div>
              <FleetTypes>
                {objectFyData?.fleetCategories?.map(
                  (fleetType: string, index: number) => {
                    return fleetType ? (
                      <div key={index}>{fleetType}</div>
                    ) : null;
                  },
                )}
              </FleetTypes>
            </Row>
          </Column>
          <VerticalLine></VerticalLine>
          <Column>
            <Row>
              <div>Email address</div>
              <div>{objectFyData?.email || 'N/A'}</div>
            </Row>
            <Row>
              <div>Phone number</div>
              <div>{objectFyData?.phoneNumber || 'N/A'}</div>
            </Row>
            <Row>
              <div>NIN No.</div>
              <div>{ninNumber || 'N/A'}</div>
            </Row>
            <Row>
              <div>Total no of fleets</div>
              <div>{objectFyData?.numberOfFleet || 'N/A'}</div>
            </Row>
          </Column>
        </GreyArea>
      </SectionInfo>

      {/* Section 2 */}
      <SectionInfo>
        <></>
        <SectionHeader>
          <LabelArea>Work History</LabelArea>
          <LabelButton>
            {!loadingWorkHistory &&
              historyInfoData &&
              historyInfoData?.currentCompaniesCount > -1 && (
                <div onClick={onShowActiveCompanyModal}>
                  View Active Company List (
                  {historyInfoData?.currentCompaniesCount})
                </div>
              )}

            {!loadingWorkHistory &&
              historyInfoData &&
              historyInfoData?.previousCompaniesCount > -1 && (
                <div onClick={onShowPreviousCompanyModal}>
                  View Previous Companies (
                  {historyInfoData?.previousCompaniesCount})
                </div>
              )}
          </LabelButton>
        </SectionHeader>

        {!loadingWorkHistory &&
        historyInfoData &&
        // historyInfoData?.currentCompaniesCount === 1 &&
        historyInfoData?.previousCompaniesCount > 0 ? (
          <GreyArea>
            <Column>
              <Row>
                <div>No of companies worked for</div>
                <div>{objectFyData?.employmentCount || 'N/A'}</div>
              </Row>
              <Row>
                <div>Last known company name</div>
                <div>
                  {objectFyData?.lastCompanyName || 'N/A'}
                  {objectFyData?.triggeredConflict && (
                    <Tooltip
                      customIcon={<WarnIcon />}
                      content='A hiring conflict was identified based on exclusivity constraints'
                    />
                  )}
                  {objectFyData?.rehired && (
                    <Tooltip
                      customIcon={<GiftBagIcon />}
                      content='Driver was rehired by a formerly exited company'
                    />
                  )}
                </div>
              </Row>
              <Row>
                <div>Start date</div>
                <div>{objectFyData?.lastStartDate || 'N/A'}</div>
              </Row>
              <Row>
                <div>End date</div>
                <div>{objectFyData?.lastEndDate || 'N/A'}</div>
              </Row>
            </Column>
            <VerticalLine></VerticalLine>
            <Column>
              <Row>
                <div>Reason for termination</div>
                <div>{objectFyData?.terminationReason || 'N/A'}</div>
              </Row>
              <Row>
                <div>Hiring eligibility</div>
                <StatusCell
                  data={{
                    status: objectFyData?.hiringEligibility ?? 'unknown',
                  }}
                  statusKey='status'
                  customClasses='small-border-radius'
                  positiveType
                />
              </Row>
              <Row>
                <div>Total theft cases reported</div>
                <div>{objectFyData?.totalThefts || '0'}</div>
              </Row>
              <Row>
                <div>Total accident cases reported</div>
                <div>{objectFyData?.totalAccidents || '0'}</div>
              </Row>
            </Column>
            <Blockquote>
              <BlockHeader>Exit Review by last known company</BlockHeader>
              <Paragraph>{`"${objectFyData?.exitReview || 'N/A'}"`}</Paragraph>
              <cite
                style={{
                  fontSize: '14px',
                  fontWeight: 400,
                }}
              >
                — {objectFyData?.reviewer ?? ''}
              </cite>
            </Blockquote>
          </GreyArea>
        ) : (
          <EmptyState
            label={'Nothing to see here yet'}
            optionalComp={'Previous company will be displayed here '}
          />
        )}
      </SectionInfo>

      <MobileViewLabelButton>
        {!loadingWorkHistory &&
          historyInfoData &&
          historyInfoData?.currentCompaniesCount > -1 && (
            <div onClick={onShowActiveCompanyModal}>
              View Active Company List ({historyInfoData?.currentCompaniesCount}
              )
            </div>
          )}

        {!loadingWorkHistory &&
          historyInfoData &&
          historyInfoData?.previousCompaniesCount > -1 && (
            <div onClick={onShowPreviousCompanyModal}>
              View Previous Companies ({historyInfoData?.previousCompaniesCount}
              )
            </div>
          )}
      </MobileViewLabelButton>

      <Modal
        showModal={showPreviousCompanyModal}
        setShowModal={setShowPreviousCompanyModal}
      >
        <DispatcherPreviousCompanyModal />
      </Modal>

      <Modal
        showModal={showActiveCompanyModal}
        setShowModal={setShowActiveCompanyModal}
      >
        <DispatcherActiveCompanyModal />
      </Modal>

      <Modal
        showModal={showKycRequiredViewActiveCompanyModal}
        setShowModal={setShowKycRequiredViewActiveCompanyModal}
      >
        <KycRequired
          successActionFn={successActionFnViewActiveCompany}
          message='You need to complete your KYC to see active company'
        />
      </Modal>

      <Modal
        showModal={showKycRequiredViewPreviousCompanyModal}
        setShowModal={setShowKycRequiredViewPreviousCompanyModal}
      >
        <KycRequired
          successActionFn={successActionFnViewPreviousCompany}
          message='You need to complete your KYC to see previous companies'
        />
      </Modal>

      <Modal
        showModal={showKycSetupViewActiveCompanyModal}
        setShowModal={setShowKycSetupViewActiveCompanyModal}
      >
        <KycInformationModal />
      </Modal>
      {/*  */}

      <Modal
        showModal={showKycSetupViewPreviousCompanyModal}
        setShowModal={setShowKycSetupViewPreviousCompanyModal}
      >
        <KycInformationModal />
      </Modal>
    </Container>
  );
};

export default DriverLookupDataBreakdown;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SectionInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  padding: 24px;
  border-radius: 8px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LabelButton = styled.div`
  display: flex;
  gap: 12px;

  & > div {
    border: 1px solid ${({ theme }) => theme.palette.borderColor};
    padding: 12px;
    border-radius: 8px;
    color: ${({ theme }) => theme.palette.blueColor};
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
  }

  @media (max-width: 884px) {
    display: none;
  }
`;

const MobileViewLabelButton = styled(LabelButton)`
  display: none;

  @media (max-width: 884px) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;
  }
`;

const GreyArea = styled.div`
  background-color: #fafafa;
  color: ${({ theme }) => theme.palette.grayTextColor};
  padding: 12px;
  grid-template-columns: 1fr 1px 1fr;
  display: grid;
  gap: 34px;

  @media (max-width: 884px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const LabelArea = styled.div`
  font-weight: 600;
  font-size: 18px;
  line-height: 30px;
`;

const FleetTypes = styled.div`
  display: flex;
  gap: 4px;

  & > div {
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid ${({ theme }) => theme.palette.borderColor};
    padding: 5.5px 11px;
    border-radius: 4px;
    height: 20px;
    font-size: 12px;
    line-height: 12px;
    color: #2f3644;
    background-color: white;
  }
`;

const VerticalLine = styled.div`
  width: 1px;
  height: 100%;
  background-color: #d1d5db;

  @media (max-width: 884px) {
    display: none;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 140px;
  font-weight: 500;
  font-size: 14px;
  line-height: 14px;

  & > div:last-of-type {
    font-weight: 600;
  }

  @media (max-width: 884px) {
    gap: 16px;
  }
`;

const Blockquote = styled.blockquote`
  width: 100%;
  grid-column: span 3;
  background-color: #fff;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 884px) {
    grid-column: span 1;
  }
`;

const BlockHeader = styled.div`
  font-weight: 500;
  font-size: 14px;
  color: #6e7079;
`;

const Paragraph = styled.p`
  font-weight: 600;
  font-size: 16px;
  color: #2f3644;
`;
